import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { startOfMonth, endOfMonth, format } from 'date-fns';

/**
 * Custom hook for schedule page data using optimized RPCs
 * Handles both single project and stack (multi-project) views
 * Reduces N×M queries to 2 batched RPC calls
 *
 * @param {object} options - Configuration options
 * @param {string} options.projectId - Single project ID
 * @param {string[]} options.projectIds - Array of project IDs for stack view
 * @param {boolean} options.isStackView - Whether this is a stack view
 * @param {Date} options.currentMonth - Current month being viewed
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} Schedule data and methods
 */
export const useScheduleData = (options = {}) => {
  const {
    projectId = null,
    projectIds = null,
    isStackView = false,
    currentMonth = new Date(),
    enabled = true
  } = options;

  // Core state
  const [space, setSpace] = useState(null);
  const [events, setEvents] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);

  // Event detail state
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [commentReactions, setCommentReactions] = useState({});

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchingEvents, setFetchingEvents] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Refs to prevent infinite loops
  const initialLoadDone = useRef(false);
  const loadingRef = useRef(false);
  const prevMonthRef = useRef(null);

  // Memoize effective project IDs
  const effectiveProjectIds = useMemo(() => {
    if (isStackView && projectIds) return projectIds;
    if (projectId) return [projectId];
    return null;
  }, [isStackView, projectIds, projectId]);

  // Transform raw data into unified event format
  const transformEvents = useCallback((data) => {
    const allEvents = [];
    const {
      scheduleEvents,
      certExpiries,
      bookings,
      tasks,
      leaveRequests,
      bankHolidays
    } = data;

    // Transform schedule events
    (scheduleEvents || []).forEach(event => {
      allEvents.push({
        id: `schedule-${event.id}`,
        sourceId: event.id,
        source: 'schedule_event',
        title: event.title,
        description: event.description,
        date: event.event_date,
        time: event.event_time,
        type: event.event_type,
        color: event.color,
        projectId: event.project_id,
        canEdit: true
      });
    });

    // Transform certificate expiries
    (certExpiries || []).forEach(cert => {
      const memberName = `${cert.member_first_name || ''} ${cert.member_last_name || ''}`.trim();
      const reqName = cert.requirement_name || 'Certificate';

      allEvents.push({
        id: `cert-expiry-${cert.id}`,
        sourceId: cert.id,
        source: 'matrix',
        title: `${reqName} - ${memberName}`,
        description: `Certificate expiry for ${memberName}`,
        date: cert.expiry_date,
        time: null,
        type: 'deadline',
        color: '#8B5CF6', // Purple
        projectId: cert.project_id,
        canEdit: false
      });
    });

    // Transform bookings (training with confirmed dates)
    (bookings || []).forEach(booking => {
      allEvents.push({
        id: `booking-${booking.id}`,
        sourceId: booking.id,
        source: 'booking',
        title: `Training: ${booking.course_name}`,
        description: `${booking.delegates || 0} delegate(s) - ${booking.booked_location || 'TBC'}`,
        date: booking.booked_date,
        time: null,
        type: 'training',
        color: '#10B981', // Green
        projectId: booking.project_id,
        canEdit: false,
        metadata: {
          status: booking.status,
          location: booking.booked_location,
          deliveryMethod: booking.booked_delivery_method,
          delegates: booking.delegates
        }
      });
    });

    // Transform tasks with due dates
    (tasks || []).forEach(task => {
      // Check if all subtasks are completed
      const subtasks = task.subtasks || [];
      const isCompleted = subtasks.length > 0 && subtasks.every(st => st.completed);
      const completedCount = subtasks.filter(st => st.completed).length;

      allEvents.push({
        id: `task-${task.id}`,
        sourceId: task.id,
        source: 'task',
        title: task.name,
        description: subtasks.length > 0
          ? `${completedCount}/${subtasks.length} subtasks completed`
          : null,
        date: task.due_date?.split('T')[0], // Extract date part from timestamp
        time: null,
        type: isCompleted ? 'other' : 'deadline',
        color: isCompleted ? '#6B7280' : '#F59E0B', // Gray if done, Orange if pending
        projectId: task.project_id,
        canEdit: false,
        metadata: {
          priority: task.priority,
          assignedTo: task.assigned_to,
          subtasks: task.subtasks,
          isCompleted
        }
      });
    });

    // Transform leave requests (multi-day events)
    (leaveRequests || []).forEach(leave => {
      const userName = `${leave.first_name || ''} ${leave.last_name || ''}`.trim();
      const leaveTypeLabels = {
        annual_leave: 'Annual Leave',
        sick_leave: 'Sick Leave',
        training: 'Training',
        personal: 'Personal',
        other: 'Leave'
      };
      const leaveLabel = leaveTypeLabels[leave.leave_type] || 'Leave';

      allEvents.push({
        id: `leave-${leave.id}`,
        sourceId: leave.id,
        source: 'leave',
        title: `${userName} - ${leaveLabel}`,
        description: null,
        date: leave.start_date,
        endDate: leave.end_date, // Multi-day support
        time: null,
        type: 'other',
        color: '#14B8A6', // Teal
        canEdit: false,
        metadata: {
          userId: leave.user_id,
          leaveType: leave.leave_type
        }
      });
    });

    // Transform bank holidays
    (bankHolidays || []).forEach(holiday => {
      allEvents.push({
        id: `holiday-${holiday.id}`,
        sourceId: holiday.id,
        source: 'holiday',
        title: holiday.name,
        description: null,
        date: holiday.date,
        time: null,
        type: 'other',
        color: '#EF4444', // Red
        canEdit: false,
        metadata: {
          countryCode: holiday.country_code
        }
      });
    });

    return allEvents;
  }, []);

  // Fetch initial page data
  const fetchPageData = useCallback(async (isInitialLoad = false) => {
    if (!enabled) return;
    if (!effectiveProjectIds || effectiveProjectIds.length === 0) return;
    if (loadingRef.current) return;

    loadingRef.current = true;

    if (isInitialLoad) {
      setInitialLoading(true);
    } else {
      setFetchingEvents(true);
    }

    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);

      const { data, error } = await supabase.rpc('get_schedule_page_data', {
        p_project_id: isStackView ? null : projectId,
        p_project_ids: isStackView ? effectiveProjectIds : null,
        p_is_stack_view: isStackView,
        p_month_start: format(monthStart, 'yyyy-MM-dd'),
        p_month_end: format(monthEnd, 'yyyy-MM-dd')
      });

      if (error) throw error;

      // Set space data (only for single project view)
      if (data.space) {
        setSpace(data.space);
      }

      // Transform and set events from all sources
      const transformedEvents = transformEvents(data);
      setEvents(transformedEvents);

      // Set project members
      setProjectMembers(data.projectMembers || []);

      initialLoadDone.current = true;
      prevMonthRef.current = format(currentMonth, 'yyyy-MM');
    } catch (err) {
      console.error('Error fetching schedule page data:', err);
      toast.error('Failed to load schedule');
    } finally {
      setInitialLoading(false);
      setFetchingEvents(false);
      loadingRef.current = false;
    }
  }, [enabled, effectiveProjectIds, isStackView, projectId, currentMonth, transformEvents]);

  // Fetch event details (comments, reactions, attachments)
  const fetchEventDetails = useCallback(async (eventId, eventSource) => {
    if (!eventId || !eventSource) return;

    setLoadingDetails(true);

    try {
      const { data, error } = await supabase.rpc('get_schedule_event_details', {
        p_event_id: eventId,
        p_event_source: eventSource
      });

      if (error) throw error;

      setComments(data.comments || []);
      setCommentReactions(data.reactions || {});
      setAttachments(data.attachments || []);
    } catch (err) {
      console.error('Error fetching event details:', err);
      toast.error('Failed to load event details');
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  // Clear event details (when closing dialog)
  const clearEventDetails = useCallback(() => {
    setComments([]);
    setAttachments([]);
    setCommentReactions({});
  }, []);

  // Refresh event details after comment/attachment changes
  const refreshEventDetails = useCallback(async (eventId, eventSource) => {
    if (!eventId || !eventSource) return;
    await fetchEventDetails(eventId, eventSource);
  }, [fetchEventDetails]);

  // Add event locally (optimistic update)
  const addEventLocally = useCallback((event) => {
    setEvents(prev => [...prev, event].sort((a, b) =>
      new Date(a.date) - new Date(b.date)
    ));
  }, []);

  // Update event locally
  const updateEventLocally = useCallback((eventId, updates) => {
    setEvents(prev => prev.map(event =>
      event.id === eventId ? { ...event, ...updates } : event
    ));
  }, []);

  // Remove event locally
  const removeEventLocally = useCallback((eventId) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
  }, []);

  // Add comment locally
  const addCommentLocally = useCallback((comment) => {
    setComments(prev => [...prev, comment]);
  }, []);

  // Remove comment locally
  const removeCommentLocally = useCallback((commentId) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
  }, []);

  // Add attachment locally
  const addAttachmentLocally = useCallback((attachment) => {
    setAttachments(prev => [attachment, ...prev]);
  }, []);

  // Remove attachment locally
  const removeAttachmentLocally = useCallback((attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  }, []);

  // Update reactions locally
  const updateReactionsLocally = useCallback((commentId, reactions) => {
    setCommentReactions(prev => ({
      ...prev,
      [commentId]: reactions
    }));
  }, []);

  // Initial load effect
  useEffect(() => {
    if (!enabled) return;
    if (!effectiveProjectIds || effectiveProjectIds.length === 0) return;

    // Only fetch on initial load
    if (!initialLoadDone.current) {
      fetchPageData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, effectiveProjectIds]);

  // Month change effect
  useEffect(() => {
    if (!enabled) return;
    if (!initialLoadDone.current) return;

    const currentMonthKey = format(currentMonth, 'yyyy-MM');
    if (prevMonthRef.current !== currentMonthKey) {
      fetchPageData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  return {
    // Data
    space,
    events,
    projectMembers,
    comments,
    attachments,
    commentReactions,

    // Loading states
    initialLoading,
    fetchingEvents,
    loadingDetails,
    loading: initialLoading || fetchingEvents,

    // Fetch methods
    fetchPageData,
    fetchEventDetails,
    clearEventDetails,
    refreshEventDetails,
    refetch: () => fetchPageData(false),
    silentRefetch: async () => {
      // Refetch without showing loading state
      if (!enabled) return;
      if (!effectiveProjectIds || effectiveProjectIds.length === 0) return;
      if (loadingRef.current) return;

      loadingRef.current = true;
      try {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const { data, error } = await supabase.rpc('get_schedule_page_data', {
          p_project_id: isStackView ? null : projectId,
          p_project_ids: isStackView ? effectiveProjectIds : null,
          p_is_stack_view: isStackView,
          p_month_start: format(monthStart, 'yyyy-MM-dd'),
          p_month_end: format(monthEnd, 'yyyy-MM-dd')
        });
        if (error) throw error;
        if (data.space) setSpace(data.space);
        const transformedEvents = transformEvents(data);
        setEvents(transformedEvents);
        setProjectMembers(data.projectMembers || []);
      } catch (err) {
        console.error('Error fetching schedule data:', err);
      } finally {
        loadingRef.current = false;
      }
    },

    // Local update methods
    addEventLocally,
    updateEventLocally,
    removeEventLocally,
    addCommentLocally,
    removeCommentLocally,
    addAttachmentLocally,
    removeAttachmentLocally,
    updateReactionsLocally,

    // Setters for direct state updates
    setEvents,
    setComments,
    setAttachments,
    setCommentReactions
  };
};
